// src/components/pages/HelpSupport.jsx
import React, { useState, useEffect } from 'react';
import SigninHeader from '../auth/SigninHeader';
import SideNavigation from './SideNavigation';
import PageHeader from '../shared/PageHeader';

const HelpSupport = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('faq');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));

    // Load user data
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const faqData = [
    {
      question: "How do I create a new bot?",
      answer: "To create a new bot, click the '+' button in the bottom right corner of your dashboard and follow the step-by-step wizard."
    },
    {
      question: "How do I integrate with Accela?",
      answer: "Go to Settings > System Access to configure your Accela environment credentials for Production, Test, and Support environments."
    },
    {
      question: "Can I customize communication templates?",
      answer: "Yes, you can customize templates in the Template Viewer. Select a template and modify the content to suit your agency's needs."
    },
    {
      question: "How do I manage team permissions?",
      answer: "Team permissions can be managed in the Team Management section where you can assign roles and control access levels."
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-main-dark text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <SigninHeader isDarkMode={isDarkMode} isLoggedIn={true} />
      
      {/* Side Navigation */}
      <SideNavigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Page Header */}
      <PageHeader 
        title="Help & Support" 
        user={user} 
        isDarkMode={isDarkMode} 
        showUserInfo={true} 
      />

      {/* Main Content */}
      <main className="ml-20 pt-[130px] pb-10 px-8">
        <div className="max-w-6xl mx-auto">
          {/* Support Tabs */}
          <div className="mb-8">
            <div className="flex space-x-1">
              {[
                { id: 'faq', label: 'FAQ' },
                { id: 'contact', label: 'Contact Support' },
                { id: 'resources', label: 'Resources' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#100E27] text-white'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`rounded-lg shadow-lg overflow-hidden ${
            isDarkMode ? 'bg-[#1F2023] border-gray-700' : 'bg-white border-gray-200'
          } border`}>
            
            {/* FAQ Tab */}
            {activeTab === 'faq' && (
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-6">
                  {faqData.map((faq, index) => (
                    <div key={index} className={`border-b pb-6 ${
                      isDarkMode ? 'border-gray-600' : 'border-gray-200'
                    }`}>
                      <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Support Tab */}
            {activeTab === 'contact' && (
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-6">Contact Support</h2>
                
                {/* Contact Methods */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className={`p-6 rounded-lg ${
                    isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                  }`}>
                    <h3 className="text-lg font-semibold mb-3">Email Support</h3>
                    <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Get help via email
                    </p>
                    <a href="mailto:support@botable.com" className="text-[#100E27] hover:underline">
                      support@botable.com
                    </a>
                  </div>
                  
                  <div className={`p-6 rounded-lg ${
                    isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                  }`}>
                    <h3 className="text-lg font-semibold mb-3">Phone Support</h3>
                    <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Monday - Friday, 9 AM - 5 PM EST
                    </p>
                    <a href="tel:+1234567890" className="text-[#100E27] hover:underline">
                      (123) 456-7890
                    </a>
                  </div>
                </div>

                {/* Support Form */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Send us a message</h3>
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Subject</label>
                        <select className={`w-full p-3 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}>
                          <option>General Question</option>
                          <option>Technical Issue</option>
                          <option>Billing Question</option>
                          <option>Feature Request</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Priority</label>
                        <select className={`w-full p-3 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}>
                          <option>Low</option>
                          <option>Medium</option>
                          <option>High</option>
                          <option>Urgent</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Message</label>
                      <textarea 
                        rows={6}
                        className={`w-full p-3 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                        placeholder="Describe your issue or question..."
                      ></textarea>
                    </div>
                    
                    <button 
                      type="submit"
                      className="bg-[#100E27] hover:bg-[#1a1a2e] text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && (
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-6">Resources</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    {
                      title: "User Guide",
                      description: "Complete guide to using Botable",
                      icon: "📖",
                      link: "#"
                    },
                    {
                      title: "API Documentation",
                      description: "Technical documentation for developers",
                      icon: "🔧",
                      link: "#"
                    },
                    {
                      title: "Video Tutorials",
                      description: "Step-by-step video guides",
                      icon: "🎥",
                      link: "#"
                    },
                    {
                      title: "Best Practices",
                      description: "Tips for optimal bot performance",
                      icon: "⭐",
                      link: "#"
                    },
                    {
                      title: "System Requirements",
                      description: "Technical requirements and compatibility",
                      icon: "⚙️",
                      link: "#"
                    },
                    {
                      title: "Release Notes",
                      description: "Latest updates and features",
                      icon: "📋",
                      link: "#"
                    }
                  ].map((resource, index) => (
                    <div key={index} className={`p-6 rounded-lg border transition-colors hover:shadow-md ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 hover:bg-gray-750' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}>
                      <div className="text-2xl mb-3">{resource.icon}</div>
                      <h3 className="text-lg font-semibold mb-2">{resource.title}</h3>
                      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {resource.description}
                      </p>
                      <a 
                        href={resource.link}
                        className="text-[#100E27] hover:underline text-sm font-medium"
                      >
                        Learn More →
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpSupport;